import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Newspaper, User, Mail, Lock, LogIn, UserPlus } from 'lucide-react';

export default function Register() {
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(formData);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputFields = [
    { label: 'FULL NAME', name: 'full_name', type: 'text', icon: User },
    { label: 'USERNAME', name: 'username', type: 'text', icon: User },
    { label: 'EMAIL', name: 'email', type: 'email', icon: Mail },
  ];

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-6 overflow-hidden bg-[#0A0C10]">
      {/* Background Aura Effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#D0021B]/10 blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/10 blur-[120px]"></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-10 w-full max-w-[480px]"
      >
        <div className="group relative bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] rounded-[2.5rem] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.02] to-transparent pointer-events-none"></div>
          
          {loading && (
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              className="absolute top-0 left-0 h-1 bg-[#D0021B] shadow-[0_0_10px_#D0021B] z-20"
            />
          )}

          <div className="px-8 pt-12 pb-10">
            <div className="text-center mb-10">
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex p-4 rounded-3xl bg-gradient-to-br from-[#D0021B] to-[#FF4D4D] shadow-[0_8px_32px_rgba(208,2,27,0.3)] mb-6"
              >
                <UserPlus className="w-10 h-10 text-white" />
              </motion.div>
              
              <h1 className="font-serif text-3xl font-bold text-white tracking-tight mb-2 uppercase">
                FACT INDIA NEWS
              </h1>
              <p className="text-white/40 text-sm tracking-widest uppercase font-bold">
                Create Admin Account
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 overflow-hidden"
                >
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-200 text-sm flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-5">
              {inputFields.map((field) => (
                <div key={field.name} className="group relative">
                  <label className={`absolute left-10 transition-all duration-300 pointer-events-none ${
                      isFocused === field.name || formData[field.name] ? '-top-3 text-[10px] text-white font-bold px-2 py-0.5 bg-[#D0021B] rounded-md z-10 scale-100 shadow-lg' : 'top-4 text-sm text-white/40'
                    }`}
                  >
                    {field.label}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <field.icon className={`w-5 h-5 transition-colors duration-300 ${isFocused === field.name ? 'text-[#D0021B]' : 'text-white/20'}`} />
                    </div>
                    <input
                      type={field.type}
                      name={field.name}
                      value={formData[field.name]}
                      onFocus={() => setIsFocused(field.name)}
                      onBlur={() => setIsFocused('')}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-4 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-2xl text-white text-sm focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                      required
                    />
                  </div>
                </div>
              ))}

              <div className="group relative">
                <label className={`absolute left-10 transition-all duration-300 pointer-events-none ${
                    isFocused === 'password' || formData.password ? '-top-3 text-[10px] text-white font-bold px-2 py-0.5 bg-[#D0021B] rounded-md z-10 scale-100 shadow-lg' : 'top-4 text-sm text-white/40'
                  }`}
                >
                  PASSWORD
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className={`w-5 h-5 transition-colors duration-300 ${isFocused === 'password' ? 'text-[#D0021B]' : 'text-white/20'}`} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onFocus={() => setIsFocused('password')}
                    onBlur={() => setIsFocused('')}
                    onChange={handleChange}
                    className="w-full pl-12 pr-12 py-4 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-2xl text-white text-sm focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.01, y: -2 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="relative w-full py-4 bg-[#D0021B] text-white font-bold rounded-2xl shadow-[0_8px_32px_rgba(208,2,27,0.3)] hover:shadow-[0_12px_48px_rgba(208,2,27,0.45)] transition-all disabled:opacity-50 group overflow-hidden mt-4"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                {loading ? 'CREATING ACCOUNT...' : 'REGISTER ACCOUNT'}
              </motion.button>
            </form>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 text-center"
            >
              <p className="text-white/40 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-[#D0021B] hover:text-[#FF4D4D] font-bold transition-colors">
                  Login here
                </Link>
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}